import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { DocumentFetchFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentFetchFailed';
import FetchDocumentTemplateFile from '@Application/Beneficiaries/Documents/Queries/FetchDocumentTemplateFile';
import fetchDocumentTemplateFile from '@Application/Beneficiaries/Documents/fetchDocumentTemplateFile';
import FetchDocument from '@Application/Beneficiaries/Documents/Queries/FetchDocument';
import fetchDocument from '@Application/Beneficiaries/Documents/fetchDocument';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import DiscardChangesDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Dialogs/DiscardChangesDialog';
import DocumentPanelSidebar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar';
import DocumentProgress from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentProgress';
import DocumentVerificationHeader from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentVerificationHeader';
import DocumentPageSkeleton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentViewer/DocumentPageSkeleton';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import useNavigationGuard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useNavigationGuard';
import MobileActionBar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/MobileActionBar';
import DocumentViewerProvider from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider';
import useDocumentListReturn from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentListReturn';
import useReviewStage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useReviewStage';
import ReviewStageNote from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ReviewStage/ReviewStageNote';
import ReviewStageTabs from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ReviewStage/ReviewStageTabs';
import useDocumentFullscreen from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentFullscreen';
import useFullscreenSync from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useFullscreenSync';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import resetDocumentVerificationSignals from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/resetDocumentVerificationSignals';
import useBeforeUnloadGuard from '@UI/Hooks/useBeforeUnloadGuard';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import useUserPermissions from '@UI/Providers/PermissionProvider/useUserPermissions';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import { FileX } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton } from '@verclam/ui-kit';
import { useSignalValue } from '@verclam/pulsar';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

// Loaded on demand: the viewer, its layout engine and its fonts weigh more than the rest of the application.
const DocumentViewer = React.lazy(
	() => import('@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentViewer'),
);

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
	max-width: 75rem;
	margin: 0 auto;
`;

/** Leaves the height of the fixed phone action bar free under the document. */
const phoneScreenStyle = css`
	padding-bottom: 5rem;
`;

/**
 * Detects a `DOCUMENT_FETCH_FAILED` rejection carrying a `404` — the one status this
 * screen renders as `EmptyState`, never `ErrorState` (`guides/state-stores.md`'s "Ce que
 * `useStoreQuery` ne garantit pas", same shape as `InterviewConsentDetailsContent`'s own guard).
 */
const isDocumentNotFoundFailure = (candidate: unknown): candidate is DocumentFetchFailedAction => {
	if (typeof candidate !== 'object' || candidate === null) {
		return false;
	}

	const action = candidate as Partial<DocumentFetchFailedAction>;

	return action.type === 'DOCUMENT_FETCH_FAILED' && action.payload?.status === 404;
};

type Props = {
	readonly subject: SubjectAddress;
	readonly documentId: number;
};

/**
 * Owns the fetches of the screen itself: the document, the dossier (this route is reached directly,
 * never nested under `BeneficiaryDetailsContent`, so its own already-loaded dossier is not
 * available here) and the template file. The document is cached for a short time: a visit within
 * that window shows the stored one, and each `documentId` change (React Router reuses this element)
 * is read again. The leaves load the lists they resolve names from — documents, interviews, agency
 * directory — and the stage tabs load the preparation.
 *
 * Also reads the template file, mounts the document viewer once the file and the user's permissions
 * are in (a late permission would rebuild the viewer and lose the page), and owns the anti-loss guards
 * (`useBeforeUnloadGuard` and `useNavigationGuard`, derived from `editingFieldSignal`), the one
 * `DiscardChangesDialog` for the whole screen, and the reset of the screen-local signals whenever
 * `documentId` changes or this component unmounts — React Router reuses this element when only
 * `:document` changes, so nothing of a previous document's session must survive.
 */
const DocumentDetailsContent: React.FC<Props> = ({ subject, documentId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const [documentNotFound, setDocumentNotFound] = React.useState(false);

	const {
		data: documentView,
		error: documentError,
		refetch: refetchDocument,
	} = useStoreQuery({
		selector: (state) => state.documentsStore.document,
		stopOn: (value) => value !== null && value.id === documentId,
		query: () =>
			dispatch(fetchDocument(new FetchDocument(facilityId, subject, documentId))).catch(
				(rejection: unknown) => {
					if (isDocumentNotFoundFailure(rejection)) {
						setDocumentNotFound(true);
					}

					throw rejection;
				},
			),
		deps: [facilityId, subject, documentId],
	});

	const {
		isKnown,
		displayName,
		error: subjectError,
		refetch: refetchSubject,
	} = useSubjectModifiability(subject);

	const reviewStage = useReviewStage(subject);
	const listReturn = useDocumentListReturn(subject);

	const templateVersionId = documentView?.templateVersionId ?? null;
	const {
		data: templateFile,
		error: templateError,
		refetch: refetchTemplate,
	} = useStoreQuery({
		selector: (state) => state.documentTemplateFilesStore.file,
		stopOn: (file) => templateVersionId === null || file?.templateVersionId === templateVersionId,
		query: () => {
			if (templateVersionId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchDocumentTemplateFile(
					new FetchDocumentTemplateFile(facilityId, subject, documentId, templateVersionId),
				),
			);
		},
		deps: [facilityId, subject, documentId, templateVersionId],
	});

	const canEdit = useCanEditDocument();
	const { isResolved: arePermissionsResolved } = useUserPermissions();
	const isCompact = useIsCompactViewport();
	const [unreadableTemplateVersionId, setUnreadableTemplateVersionId] = React.useState<number | null>(null);

	const { isFullscreen } = useDocumentFullscreen();
	useFullscreenSync();

	const editingField = useSignalValue(editingFieldSignal);
	useBeforeUnloadGuard(editingField?.isDirty ?? false);
	useNavigationGuard(editingField?.isDirty ?? false);

	// Resets whenever `documentId` genuinely changes — compared against the previous render, not just
	// present in `deps` — since React Router reuses this element when only `:document` changes: nothing
	// of a previous document's session (open panel, draft, active section, filter) must survive into
	// the next one.
	const previousDocumentIdRef = React.useRef(documentId);
	React.useEffect(() => {
		if (previousDocumentIdRef.current !== documentId) {
			resetDocumentVerificationSignals();
			previousDocumentIdRef.current = documentId;
		}
	}, [documentId]);

	// Separately resets on unmount — leaving the screen entirely, not just switching document.
	React.useEffect(() => {
		return () => {
			resetDocumentVerificationSignals();
		};
	}, []);

	const handleRetry = React.useCallback(() => {
		refetchDocument();
		refetchSubject();
	}, [refetchDocument, refetchSubject]);

	const handleViewerUnreadable = React.useCallback(() => {
		setUnreadableTemplateVersionId(templateVersionId);
	}, [templateVersionId]);

	const handleBackToDossierClick = React.useCallback(() => {
		navigate(buildSubjectPath(subject));
	}, [navigate, subject]);

	if (documentNotFound) {
		return (
			<EmptyState
				title="Document introuvable."
				icon={<FileX size={28} />}
				action={
					<Button type="button" label="Revenir au dossier" onClick={handleBackToDossierClick} />
				}
			/>
		);
	}

	if (documentError !== null || subjectError !== null) {
		return <ErrorState title="Impossible de charger le document." onRetry={handleRetry} />;
	}

	if (documentView === null || !isKnown) {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="8rem" animation="wave" shape="rectangle" />
				<Skeleton height="6rem" animation="wave" shape="rectangle" />
				<Skeleton height="6rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	const isTemplateUnreadable =
		templateError !== null ||
		(templateVersionId !== null && unreadableTemplateVersionId === templateVersionId);

	return (
		<DocumentViewerProvider document={documentView} isTemplateUnreadable={isTemplateUnreadable}>
			<div css={[screenStyle, isCompact ? phoneScreenStyle : null]}>
				<ConditionalLoader data={!isFullscreen}>
					<DocumentVerificationHeader
						subject={subject}
						dossierName={displayName ?? 'Dossier'}
						reviewStage={reviewStage}
						listReturn={listReturn}
					/>
				</ConditionalLoader>

				<ConditionalLoader data={isFullscreen ? null : reviewStage}>
					{(stage) => (
						<React.Fragment>
							<ReviewStageTabs reviewStage={stage} />
							<ReviewStageNote reviewStage={stage} />
						</React.Fragment>
					)}
				</ConditionalLoader>

				<ConditionalLoader data={!isFullscreen}>
					<DocumentProgress />
				</ConditionalLoader>

				{isTemplateUnreadable ? (
					<ErrorState
						title="Le gabarit de ce document est illisible"
						description="Le document ne peut pas être affiché ni téléchargé. Prévenez l'administrateur de votre structure : le gabarit doit être corrigé dans les réglages du groupe."
						{...(templateError !== null ? { onRetry: refetchTemplate } : {})}
					/>
				) : (
					<ConditionalLoader
						data={templateFile !== null && arePermissionsResolved ? templateFile : null}
						fallback={<DocumentPageSkeleton />}
					>
						{(file) => (
							<React.Suspense fallback={<DocumentPageSkeleton />}>
								<DocumentViewer
									key={file.templateVersionId}
									document={documentView}
									template={file.bytes}
									canEdit={canEdit}
									onUnreadable={handleViewerUnreadable}
								/>
							</React.Suspense>
						)}
					</ConditionalLoader>
				)}

				<ConditionalLoader data={!isFullscreen}>
					<MobileActionBar subject={subject} reviewStage={reviewStage} listReturn={listReturn} />
				</ConditionalLoader>

				<DocumentPanelSidebar />
				<DiscardChangesDialog />
			</div>
		</DocumentViewerProvider>
	);
};

export default DocumentDetailsContent;
